import Phaser from 'phaser';
import { GameState } from '../state/GameState';
import { ANIMAL_STATS, ALL_ANIMAL_KEYS } from '../config/animals';
import { LEVELS, MAX_LEVEL } from '../config/levels';
import { DEFAULT_CHARACTER } from '../config/presets';
import type { AnimalKey } from '../types';

// Secret debug menu — open with Ctrl+Shift+D from any scene that registers it
// (TitleScene wires the keybind). Lets you jump to any level or boss for
// testing.

export class DebugMenuScene extends Phaser.Scene {
  constructor() {
    super('DebugMenuScene');
  }

  create(): void {
    const W = this.scale.width;
    const H = this.scale.height;
    this.add.rectangle(0, 0, W, H, 0x000000, 0.92).setOrigin(0, 0);
    this.add
      .text(W / 2, 50, 'DEBUG / TEST MENU', {
        fontFamily: 'sans-serif',
        fontSize: '36px',
        color: '#f2c14e',
      })
      .setOrigin(0.5);
    this.add
      .text(W / 2, 88, 'Pick an animal, then jump to any level or boss.', {
        fontFamily: 'sans-serif',
        fontSize: '16px',
        color: '#bcd0e0',
      })
      .setOrigin(0.5);

    // Make sure we have a character + animal so the runner doesn't crash.
    if (!this.registry.get('character')) {
      GameState.setCharacter(this.registry, DEFAULT_CHARACTER);
    }
    if (!this.registry.get('animal')) {
      GameState.setAnimal(this.registry, 'horse');
    }

    // Animal picker row
    this.add
      .text(40, 130, 'Animal:', {
        fontFamily: 'sans-serif',
        fontSize: '20px',
        color: '#ffffff',
      });
    let selectedAnimal: AnimalKey = GameState.getAnimal(this.registry);
    const animalLabel = this.add.text(120, 130, '', {
      fontFamily: 'sans-serif',
      fontSize: '20px',
      color: '#ffd24a',
    });
    const renderAnimal = () => animalLabel.setText(ANIMAL_STATS[selectedAnimal].displayName);
    renderAnimal();
    this.makeBtn(W - 180, 130, '◀', () => {
      const idx = ALL_ANIMAL_KEYS.indexOf(selectedAnimal);
      selectedAnimal = ALL_ANIMAL_KEYS[(idx - 1 + ALL_ANIMAL_KEYS.length) % ALL_ANIMAL_KEYS.length];
      GameState.setAnimal(this.registry, selectedAnimal);
      renderAnimal();
    });
    this.makeBtn(W - 80, 130, '▶', () => {
      const idx = ALL_ANIMAL_KEYS.indexOf(selectedAnimal);
      selectedAnimal = ALL_ANIMAL_KEYS[(idx + 1) % ALL_ANIMAL_KEYS.length];
      GameState.setAnimal(this.registry, selectedAnimal);
      renderAnimal();
    });

    // Level grid
    this.add
      .text(40, 180, 'Levels (★ = boss)', {
        fontFamily: 'sans-serif',
        fontSize: '20px',
        color: '#ffffff',
      });

    const cols = 10;
    const cell = 60;
    const startX = 40;
    const startY = 240;
    for (let n = 1; n <= MAX_LEVEL; n++) {
      const cfg = LEVELS[n];
      const cx = startX + ((n - 1) % cols) * cell;
      const cy = startY + Math.floor((n - 1) / cols) * cell;
      const isBoss = cfg.hasBossAtEnd;
      const c = this.add.container(cx, cy);
      const bg = this.add
        .rectangle(0, 0, cell - 8, cell - 8, isBoss ? 0x6b1a1a : 0x3d2914)
        .setStrokeStyle(2, 0xffffff);
      const t = this.add
        .text(0, -6, String(n), {
          fontFamily: 'sans-serif',
          fontSize: '20px',
          color: '#ffffff',
        })
        .setOrigin(0.5);
      const sub = this.add
        .text(0, 14, isBoss ? `★ ${cfg.theme}` : cfg.theme, {
          fontFamily: 'sans-serif',
          fontSize: '10px',
          color: '#ffd9a0',
        })
        .setOrigin(0.5);
      c.add([bg, t, sub]);
      c.setSize(cell - 8, cell - 8).setInteractive({ useHandCursor: true });
      c.on('pointerover', () => bg.setFillStyle(isBoss ? 0xa83333 : 0x6b3410));
      c.on('pointerout', () => bg.setFillStyle(isBoss ? 0x6b1a1a : 0x3d2914));
      c.on('pointerdown', () => {
        GameState.setLevel(this.registry, n);
        this.scene.start('GameScene', { level: n });
      });
    }

    // Boss-only quick jumps
    const bossY = startY + Math.ceil(MAX_LEVEL / cols) * cell + 30;
    this.add
      .text(40, bossY, 'Jump straight to boss:', {
        fontFamily: 'sans-serif',
        fontSize: '20px',
        color: '#ffffff',
      });
    [5, 10, 15, 20].forEach((lvl, i) => {
      const x = 360 + i * 130;
      const c = this.add.container(x, bossY + 10);
      const bg = this.add.rectangle(0, 0, 110, 36, 0x6b1a1a).setStrokeStyle(2, 0xffffff);
      const t = this.add
        .text(0, 0, `Boss Lv ${lvl}`, {
          fontFamily: 'sans-serif',
          fontSize: '16px',
          color: '#ffffff',
        })
        .setOrigin(0.5);
      c.add([bg, t]);
      c.setSize(110, 36).setInteractive({ useHandCursor: true });
      c.on('pointerover', () => bg.setFillStyle(0xa83333));
      c.on('pointerout', () => bg.setFillStyle(0x6b1a1a));
      c.on('pointerdown', () => {
        GameState.setLevel(this.registry, lvl);
        this.scene.start('BossScene', { level: lvl });
      });
    });

    // Times-tables jump
    const ttY = bossY + 70;
    this.add
      .text(40, ttY, 'Other:', {
        fontFamily: 'sans-serif',
        fontSize: '20px',
        color: '#ffffff',
      });
    const ttBtn = this.add.container(280, ttY + 10);
    const ttBg = this.add.rectangle(0, 0, 160, 36, 0x3d2914).setStrokeStyle(2, 0xffffff);
    const ttT = this.add
      .text(0, 0, 'Times Tables', {
        fontFamily: 'sans-serif',
        fontSize: '16px',
        color: '#ffffff',
      })
      .setOrigin(0.5);
    ttBtn.add([ttBg, ttT]);
    ttBtn.setSize(160, 36).setInteractive({ useHandCursor: true });
    ttBtn.on('pointerover', () => ttBg.setFillStyle(0x6b3410));
    ttBtn.on('pointerout', () => ttBg.setFillStyle(0x3d2914));
    ttBtn.on('pointerdown', () => {
      this.scene.start('TimesTableScene', {
        next: 'LevelIntroScene',
        nextData: { level: 2 },
      });
    });

    // Win screen jump
    const winBtn = this.add.container(460, ttY + 10);
    const winBg = this.add.rectangle(0, 0, 160, 36, 0x3d2914).setStrokeStyle(2, 0xffffff);
    const winT = this.add
      .text(0, 0, 'Win Screen', {
        fontFamily: 'sans-serif',
        fontSize: '16px',
        color: '#ffffff',
      })
      .setOrigin(0.5);
    winBtn.add([winBg, winT]);
    winBtn.setSize(160, 36).setInteractive({ useHandCursor: true });
    winBtn.on('pointerover', () => winBg.setFillStyle(0x6b3410));
    winBtn.on('pointerout', () => winBg.setFillStyle(0x3d2914));
    winBtn.on('pointerdown', () => {
      this.scene.start('WinScene');
    });

    // Close
    const closeBtn = this.add.container(W - 60, 50);
    const closeBg = this.add.circle(0, 0, 22, 0x3d2914).setStrokeStyle(2, 0xffffff);
    const closeT = this.add
      .text(0, 0, '✕', { fontFamily: 'sans-serif', fontSize: '20px', color: '#ffffff' })
      .setOrigin(0.5);
    closeBtn.add([closeBg, closeT]);
    closeBtn.setSize(44, 44).setInteractive({ useHandCursor: true });
    closeBtn.on('pointerdown', () => {
      this.scene.start('TitleScene');
    });

    // Footer
    this.add
      .text(W / 2, H - 20, 'Open this menu any time with Ctrl+Shift+D from the title screen.', {
        fontFamily: 'sans-serif',
        fontSize: '12px',
        color: '#888',
      })
      .setOrigin(0.5);
  }

  private makeBtn(x: number, y: number, glyph: string, onClick: () => void): void {
    const t = this.add
      .text(x, y, glyph, { fontFamily: 'sans-serif', fontSize: '24px', color: '#ffffff' })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    t.on('pointerdown', onClick);
  }
}
